"use client";

import Title from "@/components/common/Typography/Title";
import { useTERouter } from "@/hooks/app/useTERouter";
import { useEsimCatalogFilters } from "@/hooks/esim/useEsimCatalogFilters";
import {
  faChevronLeft,
  faChevronRight,
  faGlobe,
  faLink,
  faLocationDot,
  faSignal,
  faSimCard,
  faTowerBroadcast,
  faWifi,
} from "@fortawesome/free-solid-svg-icons";
import { FontAwesomeIcon, FontAwesomeIconProps } from "@fortawesome/react-fontawesome";
import { Skeleton } from "antd";
import { FC, useRef } from "react";
import { useTranslation } from "react-i18next";

/** Xoay vòng vài icon liên tưởng đến kết nối/eSIM cho đỡ đơn điệu — không gắn theo ý nghĩa riêng từng nhóm. */
const GROUP_ICONS: FontAwesomeIconProps["icon"][] = [faSimCard, faWifi, faGlobe, faTowerBroadcast, faSignal];

const SCROLL_STEP = 320;
const SKELETON_CARD_COUNT = 6;

const EsimDiscoverByGroup: FC = () => {
  const { t } = useTranslation();
  const router = useTERouter();
  const { parentGroupNameOptions, loading } = useEsimCatalogFilters();
  const scrollRef = useRef<HTMLDivElement>(null);

  // Tip box không phụ thuộc data API — hiện y hệt ở cả trạng thái loading lẫn đã có data.
  const tipBlock = (
    <div className="esim-hub__discover-group-tip">
      <div className="esim-hub__discover-group-tip-icons">
        <span className="esim-hub__discover-group-tip-icon">
          <FontAwesomeIcon icon={faLocationDot} />
        </span>
        <FontAwesomeIcon icon={faLink} className="esim-hub__discover-group-tip-arrow" />
        <span className="esim-hub__discover-group-tip-icon esim-hub__discover-group-tip-icon--primary">
          <FontAwesomeIcon icon={faSimCard} />
        </span>
        <FontAwesomeIcon icon={faLink} className="esim-hub__discover-group-tip-arrow" />
        <span className="esim-hub__discover-group-tip-icon">
          <FontAwesomeIcon icon={faLocationDot} />
        </span>
      </div>
      <div className="esim-hub__discover-group-tip-title">{t("esim.discover_multi_country_title")}</div>
      <div className="esim-hub__discover-group-tip-desc">
        <div>{t("esim.discover_multi_country_desc_line1")}</div>
        <div>{t("esim.discover_multi_country_desc_line2")}</div>
      </div>
    </div>
  );

  if (loading) {
    return (
      <section className="esim-hub__discover-group" aria-hidden>
        <Title level={3} style={{ marginTop: 0, marginBottom: 0 }}>
          {t("esim.discover_by_group_title")}
        </Title>
        <div className="esim-hub__discover-group-row">
          <div className="esim-hub__discover-group-wrap">
            <div className="esim-hub__discover-group-wrap-cta">{t("esim.discover_multi_country_cta")}</div>
            <div className="esim-hub__discover-group-scroll-area">
              <div className="esim-hub__discover-group-scroll">
                {Array.from({ length: SKELETON_CARD_COUNT }).map((_, idx) => (
                  <div className="esim-hub__discover-group-card" key={idx} style={{ cursor: "default" }}>
                    <Skeleton.Avatar active shape="circle" size={54} />
                    <Skeleton
                      active
                      title={false}
                      paragraph={{ rows: 1, width: "80%" }}
                      style={{ marginTop: 8, width: "100%" }}
                    />
                  </div>
                ))}
              </div>
            </div>
          </div>

          {tipBlock}
        </div>
      </section>
    );
  }

  if (!parentGroupNameOptions.length) return null;

  const goToGroup = (groupName: string) => {
    router.push(`/esim-du-lich/ket-qua-tim-kiem?search=${encodeURIComponent(groupName)}`);
  };

  const scrollBy = (direction: 1 | -1) => {
    scrollRef.current?.scrollBy({ left: direction * SCROLL_STEP, behavior: "smooth" });
  };

  return (
    <section className="esim-hub__discover-group" aria-labelledby="esim-discover-group-heading">
      <Title id="esim-discover-group-heading" level={3} style={{ marginTop: 0, marginBottom: 0 }}>
        {t("esim.discover_by_group_title")}
      </Title>
      <div className="esim-hub__discover-group-row">
        <div className="esim-hub__discover-group-wrap">
          <div className="esim-hub__discover-group-wrap-cta">{t("esim.discover_multi_country_cta")}</div>
          <div className="esim-hub__discover-group-scroll-area">
            <button
              type="button"
              aria-label="Scroll left"
              className="esim-hub__discover-group-nav-btn esim-hub__discover-group-nav-btn--prev"
              onClick={() => scrollBy(-1)}
            >
              <FontAwesomeIcon icon={faChevronLeft} />
            </button>
            <div className="esim-hub__discover-group-scroll" ref={scrollRef}>
              {parentGroupNameOptions.map((groupName, index) => (
                <button
                  key={groupName}
                  type="button"
                  className="esim-hub__discover-group-card"
                  onClick={() => goToGroup(groupName)}
                >
                  <span className="esim-hub__discover-group-card-icon">
                    <FontAwesomeIcon icon={GROUP_ICONS[index % GROUP_ICONS.length]} />
                  </span>
                  <span className="esim-hub__discover-group-card-name">{groupName}</span>
                </button>
              ))}
            </div>
            <button
              type="button"
              aria-label="Scroll right"
              className="esim-hub__discover-group-nav-btn esim-hub__discover-group-nav-btn--next"
              onClick={() => scrollBy(1)}
            >
              <FontAwesomeIcon icon={faChevronRight} />
            </button>
          </div>
        </div>

        {tipBlock}
      </div>
    </section>
  );
};

export default EsimDiscoverByGroup;
